import { useMemo, useReducer } from 'react'
import { WalletStatus } from '~/lib/useWallet'
import { BigNumber, Contract, ethers } from 'ethers'
import EarnABI from './abi/earn.json'
import ERC20ABI from './abi/erc20.json'
import PancakePairABI from './abi/IPancakePair.json'
import PriceFeed from './abi/priceFeed.json'
import {
	StaticJsonRpcProvider,
	TransactionResponse,
	Web3Provider,
} from '@ethersproject/providers'
import { apr, apy, toFloat } from '~/contracts/utils'
import { ChainConfig, EarnConfig } from '~/types/config'

export enum EarnStatus {
	NONE,
	PENDING,
	FAILED,
	NO_DEPLOYED,
	SUCCESS,
}

type EarnAction =
	| { type: 'pending' }
	| { type: 'success' }
	| { type: 'failed' }
	| { type: 'reset' }
	| { type: 'noDeployed' }
	| { type: 'earn'; earn: EarnInfo }
	| { type: 'token'; token: TokenInfo }

type EarnPoolInfo = {
	lpAddress: string
	allocPoint: BigNumber
	lastRewardBlock: BigNumber
	accRewardPerShare: BigNumber
}

type UserInfo = {
	amount: BigNumber
	rewardDebt: BigNumber
}

export type EarnInfo = {
	rewardPerBlock: BigNumber
	totalAllocPoint: BigNumber
	pendingReward: BigNumber
	poolInfo: EarnPoolInfo
	ethPriceUSD: BigNumber
	userInfo: UserInfo
}

export type TokenInfo = {
	name: string
	decimals: number
	symbol: string
	allowance: BigNumber
	balanceOf: BigNumber
	balanceOfMaster: BigNumber
	totalSupply: BigNumber
	reserves: [BigNumber, BigNumber]
}

export type EarnState = {
	status: EarnStatus
	earn: EarnInfo
	token: TokenInfo
}

const initializeEarnState: EarnState = {
	status: EarnStatus.NONE,
	earn: undefined,
	token: undefined,
}

const earnReducer = (state: EarnState, action: EarnAction): EarnState => {
	switch (action.type) {
		case 'pending':
			return { ...state, status: EarnStatus.PENDING }
		case 'failed':
			return { ...state, status: EarnStatus.FAILED }
		case 'reset':
			return { ...initializeEarnState, status: EarnStatus.NONE }
		case 'success':
			return { ...state, status: EarnStatus.SUCCESS }
		case 'noDeployed':
			return { ...state, status: EarnStatus.NO_DEPLOYED }
		case 'earn':
			return { ...state, earn: action.earn }
		case 'token':
			return { ...state, token: action.token }
	}
	return state
}

export type EarnHook = {
	apr: () => number
	apy: () => number
	priceOf: (amount: BigNumber) => number
	status: EarnStatus
	earn: EarnInfo
	token: TokenInfo
	load: () => void
	refresh: () => void
	reset: () => void
	approve: (amount: BigNumber) => Promise<TransactionResponse>
	deposit: (amount: BigNumber) => Promise<TransactionResponse>
	withdraw: (amount: BigNumber) => Promise<TransactionResponse>
}

interface EarnHookParams {
	account: string
	chainConfig: ChainConfig
	earnConfig: EarnConfig
	provider: Web3Provider | StaticJsonRpcProvider
	status: WalletStatus
}

export const useEarnContract = ({
	account,
	chainConfig,
	earnConfig,
	provider,
	status,
}: EarnHookParams): EarnHook => {
	let [state, dispatch] = useReducer(earnReducer, initializeEarnState)

	// priceFeedLatestAnswer
	const priceFeedLatestAnswer = (): Promise<BigNumber> => {
		// if not mainnet -> resolve to 0
		return chainConfig.priceFeed
			? new ethers.Contract(
					chainConfig.priceFeed,
					PriceFeed,
					provider
			  ).latestAnswer()
			: Promise.resolve(BigNumber.from(0))
	}

	//// Earn contract
	const earnContract = useMemo(() => {
		let c = new ethers.Contract(earnConfig.address, EarnABI, provider)
		return status === WalletStatus.CONNECTED
			? c.connect(provider.getSigner(account))
			: c
	}, [account, provider, earnConfig.address, status])
	// pendingReward
	const earnPendingReward = (): Promise<BigNumber> =>
		earnContract.pendingReward(earnConfig.context.pid, account)
	// rewardPerBlock
	const earnRewardPerBlock = (): Promise<BigNumber> =>
		earnContract.rewardPerBlock()
	// poolInfo
	const earnPoolInfo = (): Promise<
		[
			lpAddress: string,
			allocPoint: BigNumber,
			lastRewardBlock: BigNumber,
			accRewardPerShare: BigNumber
		]
	> => earnContract.poolInfo(earnConfig.context.pid)
	// userInfo
	const earnUserInfo = (): Promise<
		[amount: BigNumber, rewardDebt: BigNumber]
	> => earnContract.userInfo(earnConfig.context.pid, account)
	// totalAllocPoint
	const earnTotalAllocPoint = (): Promise<BigNumber> =>
		earnContract.totalAllocPoint()
	// deposit
	const earnDeposit = (amount: BigNumber): Promise<TransactionResponse> =>
		earnContract.deposit(earnConfig.context.pid, amount)
	// withdraw
	const earnWithdraw = (amount: BigNumber): Promise<TransactionResponse> =>
		earnContract.withdraw(earnConfig.context.pid, amount)

	//// Token contract
	const tokenContract = (address: string) =>
		new ethers.Contract(address, ERC20ABI, provider).connect(
			provider.getSigner(account)
		)
	// BalanceOf
	const tokenBalanceOf = (
		contract: Contract,
		holder: string
	): Promise<BigNumber> => contract.balanceOf(holder)
	// Allowance
	const tokenAllowance = (contract: Contract): Promise<BigNumber> =>
		contract.allowance(account, earnConfig.address)
	// info: name, symbol, decimals, totalSupply
	const tokenInfo = (
		contract: Contract
	): Promise<[string, string, number, BigNumber]> =>
		Promise.all([
			contract.name(),
			contract.symbol(),
			contract.decimals(),
			contract.totalSupply(),
		])

	// Pair contract
	// reserves: reserve0, reserve1
	const pairReserves = (address: string): Promise<[BigNumber, BigNumber]> =>
		new ethers.Contract(address, PancakePairABI, provider)
			.connect(provider.getSigner(account))
			.getReserves()

	const loadToken = (tc: Contract) =>
		Promise.all([
			tokenInfo(tc),
			tokenAllowance(tc),
			tokenBalanceOf(tc, account),
			tokenBalanceOf(tc, earnConfig.address),
			pairReserves(tc.address),
		])
			.then(
				([
					tokenInfo,
					allowance,
					balanceOf,
					balanceOfMaster,
					reserves,
				]) => {
					dispatch({
						type: 'token',
						token: {
							name: tokenInfo[0],
							symbol: tokenInfo[1],
							decimals: tokenInfo[2],
							totalSupply: tokenInfo[3],
							allowance,
							balanceOf,
							balanceOfMaster,
							reserves,
						},
					})
				}
			)
			.catch((err) => console.log(err))

	const refreshEarn = () => {
		return earnContract
			.deployed()
			.then(() => {
				Promise.all([
					earnPendingReward(),
					earnRewardPerBlock(),
					earnPoolInfo(),
					earnUserInfo(),
					earnTotalAllocPoint(),
					priceFeedLatestAnswer(),
				])
					.then(
						([
							pendingReward,
							rewardPerBlock,
							poolInfo,
							userInfo,
							totalAllocPoint,
							ethPriceUSD,
						]) =>
							loadToken(tokenContract(poolInfo[0])).then(() => {
								dispatch({
									type: 'earn',
									earn: {
										pendingReward,
										rewardPerBlock,
										ethPriceUSD,
										poolInfo: {
											lpAddress: poolInfo[0],
											allocPoint: poolInfo[1],
											lastRewardBlock: poolInfo[2],
											accRewardPerShare: poolInfo[3],
										},
										userInfo: {
											amount: userInfo[0],
											rewardDebt: userInfo[1],
										},
										totalAllocPoint,
									},
								})
							})
					)
					.then(() => dispatch({ type: 'success' }))
					.catch((err) => {
						console.error(err)
						dispatch({ type: 'failed' })
					})
			})
			.catch((err) => {
				console.error(err)
				dispatch({ type: 'noDeployed' })
			})
	}

	const loadEarn = () => {
		dispatch({ type: 'pending' })
		return refreshEarn()
	}

	// TODO fix hardcoded decimals (state.token.decimals)
	const localToFloat = (value: BigNumber) =>
		toFloat(value, state.token.decimals)

	const priceSIS = () =>
		localToFloat(state.token.reserves[0]) /
		localToFloat(state.token.reserves[1])

	const reloadUserInfo = (tx: TransactionResponse) =>
		tx
			.wait()
			.then(() =>
				earnUserInfo().then(([newAmount, newRewardDebt]) => {
					dispatch({
						type: 'earn',
						earn: {
							...state.earn,
							userInfo: {
								amount: newAmount,
								rewardDebt: newRewardDebt,
							},
						},
					})
				})
			)
			.then(() => tx)

	const localAPR = () =>
		apr(state.token, state.earn, earnConfig.context.secondsPerBlock)

	return {
		status: state.status,
		earn: state.earn,
		token: state.token,
		apr: () => localAPR(),
		apy: () => apy(localAPR()),
		priceOf: (amount) => {
			const ethPriceDecimals = 1e8 // TODO get from chainlink datafeed contract
			const ethPriceUSD =
				state.earn.ethPriceUSD.toNumber() / ethPriceDecimals
			return localToFloat(amount) * priceSIS() * ethPriceUSD
		},
		reset: () => dispatch({ type: 'pending' }),
		load: () => loadEarn(),
		refresh: () => refreshEarn(),
		approve: (amount) => {
			return tokenContract(state.earn.poolInfo.lpAddress)
				.approve(earnConfig.address, amount)
				.then((tx) =>
					tx
						.wait()
						.then(() => {
							dispatch({
								type: 'token',
								token: { ...state.token, allowance: amount },
							})
						})
						.then(() => tx)
				)
		},
		deposit: (amount) => earnDeposit(amount).then(reloadUserInfo),
		withdraw: (amount) => earnWithdraw(amount).then(reloadUserInfo),
	}
}
